import {
  Component, Output, EventEmitter, ViewChild, ElementRef,
  ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ChatStateService } from '../../store/chat-state.service';
import { ChatAttachment } from '../../models/chat.models';

export interface SendEvent {
  text: string;
  attachments: ChatAttachment[];
}

@Component({
  selector: 'app-chat-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="xci-wrap">

      <!-- Attachment chips -->
      <div class="xci-attachments" *ngIf="attachments.length">
        <div class="xci-chip" *ngFor="let a of attachments; let i = index">
          <!-- file icon -->
          <svg viewBox="0 0 14 14" fill="currentColor" width="10" height="10" style="flex-shrink:0">
            <path d="M3 1.5A1.5 1.5 0 014.5 0h5.379a1.5 1.5 0 011.06.44l2.122 2.12A1.5 1.5 0 0113.5 3.62V12.5A1.5 1.5 0 0112 14H4.5A1.5 1.5 0 013 12.5V1.5z"/>
          </svg>
          <span class="xci-chip-name">{{ a.name }}</span>
          <button class="xci-chip-del" (click)="removeAtt(i)" title="Remove">
            <svg viewBox="0 0 12 12" fill="currentColor" width="9" height="9">
              <path d="M2.22 2.22a.75.75 0 011.06 0L6 4.94l2.72-2.72a.75.75 0 111.06 1.06L7.06 6l2.72 2.72a.75.75 0 11-1.06 1.06L6 7.06 3.28 9.78a.75.75 0 01-1.06-1.06L4.94 6 2.22 3.28a.75.75 0 010-1.06z"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Composer box: border wraps attach + textarea + send together -->
      <div class="xci-composer" [class.xci-composer--focus]="focused" [class.xci-composer--disabled]="isStreaming">

        <!-- Attach button (left, vertically centred) -->
        <button
          class="xci-attach-btn"
          title="Attach file  (.txt .md .csv .json .pdf .png .jpg)"
          [disabled]="isStreaming || controllerUnresolved"
          (click)="fileEl.click()">
          <!-- paperclip -->
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6"
               stroke-linecap="round" stroke-linejoin="round" width="18" height="18">
            <path d="M17.5 10.833L10 18.333a5.833 5.833 0 01-8.25-8.25l7.917-7.916a3.889 3.889 0 015.5 5.5L7.25 15.583a1.944 1.944 0 01-2.75-2.75L11.667 5.667"/>
          </svg>
        </button>
        <input #fileEl type="file" hidden multiple
               accept=".txt,.md,.csv,.json,.pdf,.png,.jpg,.jpeg,.webp"
               (change)="onFileSelect($event)"/>

        <!-- Textarea (grows with content) -->
        <textarea
          chatAutoResize
          [minRows]="1"
          [maxRows]="5"
          #ta
          [(ngModel)]="text"
          placeholder="Ask anything about your xedge controller…"
          [disabled]="isStreaming || controllerUnresolved"
          class="xci-ta"
          (keydown)="onKey($event)"
          (focus)="focused=true"
          (blur)="focused=false"
        ></textarea>

        <!-- Send / Stop button (right, vertically centred) -->
        <button
          *ngIf="isStreaming"
          class="xci-send-btn xci-send-btn--stop"
          (click)="onStop()"
          title="Stop generating">
          <!-- stop square -->
          <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15">
            <rect x="5" y="5" width="10" height="10" rx="2"/>
          </svg>
        </button>
        <button
          *ngIf="!isStreaming"
          class="xci-send-btn"
          [class.xci-send-btn--active]="text.trim().length > 0"
          [disabled]="!text.trim() || controllerUnresolved"
          (click)="send()"
          title="Send (Enter)">
          <!-- arrow up -->
          <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15">
            <path fill-rule="evenodd"
              d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z"
              clip-rule="evenodd"/>
          </svg>
        </button>
      </div>

      <!-- Hint row -->
      <div class="xci-hint-row">
        <span class="xci-hint-item"><kbd>↵</kbd> send</span>
        <span class="xci-hint-sep">·</span>
        <span class="xci-hint-item"><kbd>⇧ ↵</kbd> new line</span>
      </div>

    </div>
  `,
  styles: [`
    /* ── wrapper ──────────────────────────────────────────────────────── */
    .xci-wrap {
      padding: 8px 14px 10px;
      border-top: 1px solid #e8edf5;
      background: #fff;
    }

    /* ── attachment chips ─────────────────────────────────────────────── */
    .xci-attachments {
      display: flex; flex-wrap: wrap; gap: 5px; margin-bottom: 8px;
    }
    .xci-chip {
      display: flex; align-items: center; gap: 5px;
      background: #eef2ff; border: 1px solid #c7d2fe;
      border-radius: 999px; padding: 3px 8px 3px 7px;
      font-size: 11.5px; color: #3730a3;
      max-width: 180px;
    }
    .xci-chip-name {
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      max-width: 120px;
    }
    .xci-chip-del {
      background: none; border: none; cursor: pointer;
      color: #a5b4fc; display: flex; align-items: center; padding: 0;
      flex-shrink: 0; border-radius: 50%;
      transition: color .12s;
    }
    .xci-chip-del:hover { color: #4f46e5; }

    /* ── composer box (unified border) ───────────────────────────────── */
    .xci-composer {
      display: flex;
      align-items: center;          /* all children bottom-aligned by default */
      gap: 4px;
      border: 1.5px solid #e2e8f0;
      border-radius: 14px;
      background: #f8fafc;
      padding: 6px 6px 6px 4px;
      transition: border-color .18s, box-shadow .18s;
    }
    .xci-composer--focus {
      border-color: #6366f1;
      box-shadow: 0 0 0 3px rgba(99,102,241,.10);
      background: #fff;
    }
    .xci-composer--disabled {
      opacity: .6;
    }

    /* ── attach button ────────────────────────────────────────────────── */
    .xci-attach-btn {
      flex-shrink: 0;
      background: none; border: none; cursor: pointer;
      color: #94a3b8;
      width: 34px; height: 34px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 9px;
      margin-bottom: 1px;            /* nudge to optically align with send btn */
      transition: color .14s, background .14s;
    }
    .xci-attach-btn:hover:not(:disabled) {
      color: #6366f1; background: #eef2ff;
    }
    .xci-attach-btn:disabled { cursor: not-allowed; opacity: .4; }

    /* ── textarea ─────────────────────────────────────────────────────── */
    .xci-ta {
      flex: 1;
      border: none; outline: none;
      background: transparent;
      resize: none;
      font-size: 13.5px; font-family: inherit;
      color: #1e2d50; line-height: 1.55;
      padding: 5px 4px;             /* internal padding only */
      min-width: 0;
      align-self: flex-end;         /* keeps caret near bottom when multiline */
    }
    .xci-ta::placeholder { color: #b0bac9; }
    .xci-ta:disabled { cursor: not-allowed; }

    /* ── send / stop button ───────────────────────────────────────────── */
    .xci-send-btn {
      flex-shrink: 0;
      width: 34px; height: 34px;
      border: none; border-radius: 10px; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 1px;
      background: #e2e8f0;          /* disabled-look by default */
      color: #94a3b8;
      transition: background .15s, color .15s, transform .12s;
    }
    .xci-send-btn--active {
      background: linear-gradient(135deg, #6366f1, #4f7ef7);
      color: #fff;
    }
    .xci-send-btn--active:hover { transform: scale(1.07); opacity: .9; }
    .xci-send-btn:disabled { cursor: not-allowed; }
    .xci-send-btn--stop {
      background: linear-gradient(135deg, #ef4444, #f87171);
      color: #fff;
    }
    .xci-send-btn--stop:hover { transform: scale(1.07); }

    /* ── hint row ─────────────────────────────────────────────────────── */
    .xci-hint-row {
      display: flex; align-items: center; justify-content: flex-end;
      gap: 5px; margin-top: 5px;
    }
    .xci-hint-item {
      display: flex; align-items: center; gap: 3px;
      font-size: 10.5px; color: #b0bac9;
    }
    .xci-hint-sep { color: #d1d5db; font-size: 10px; }
    kbd {
      display: inline-flex; align-items: center;
      background: #f1f5f9; border: 1px solid #e2e8f0;
      border-bottom-width: 2px;
      border-radius: 4px;
      padding: 1px 5px;
      font-size: 10px; font-family: inherit;
      color: #6b7280; line-height: 1.4;
    }
  `]
})
export class ChatInputComponent implements OnInit, OnDestroy {
  @Output() messageSent = new EventEmitter<SendEvent>();
  @Output() stopStream  = new EventEmitter<void>();

  @ViewChild('ta') taRef!: ElementRef<HTMLTextAreaElement>;

  text        = '';
  attachments: ChatAttachment[] = [];
  isStreaming = false;
  focused     = false;
  controllerUnresolved = false;   // ← new

  private sub!: Subscription;

  constructor(private chatState: ChatStateService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.sub = this.chatState.state$.subscribe(s => {
      this.isStreaming = s.isStreaming;
      this.controllerUnresolved = s.controllerUnresolved;   // ← new
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void { this.sub?.unsubscribe(); }

  onKey(evt: KeyboardEvent): void {
    if (evt.key === 'Enter' && !evt.shiftKey) {
      evt.preventDefault();
      this.send();
    }
  }

  send(): void {
    const t = this.text.trim();
    if (!t || this.isStreaming || this.controllerUnresolved) return;
    this.messageSent.emit({ text: t, attachments: [...this.attachments] });
    this.text = '';
    this.attachments = [];
    setTimeout(() => this.taRef?.nativeElement?.focus(), 0);
  }

  onStop(): void { this.stopStream.emit(); }

  async onFileSelect(evt: Event): Promise<void> {
    const input = evt.target as HTMLInputElement;
    if (!input.files) return;
    for (const f of Array.from(input.files)) {
      const kind: 'image' | 'text' = f.type.startsWith('image/') ? 'image' : 'text';
      const data = await this.readFile(f, kind);
      this.attachments.push({ name: f.name, media_type: f.type || 'text/plain', kind, data });
    }
    this.cdr.markForCheck();
    input.value = '';
  }

  removeAtt(i: number): void {
    this.attachments.splice(i, 1);
    this.cdr.markForCheck();
  }

  private readFile(f: File, kind: 'image' | 'text'): Promise<string> {
    return new Promise((res, rej) => {
      const r = new FileReader();
      if (kind === 'image') {
        r.onload = () => res((r.result as string).split(',')[1]);
        r.readAsDataURL(f);
      } else {
        r.onload = () => res(r.result as string);
        r.readAsText(f);
      }
      r.onerror = rej;
    });
  }
}